import { Desktop, DeviceMobile, DeviceTablet, Question } from "@phosphor-icons/react";

const A3_TO_A2 = {
  ita: "IT", che: "CH", fra: "FR", deu: "DE", aut: "AT", esp: "ES", prt: "PT", gbr: "GB",
  irl: "IE", bel: "BE", nld: "NL", lux: "LU", dnk: "DK", swe: "SE", nor: "NO", fin: "FI",
  pol: "PL", cze: "CZ", svk: "SK", svn: "SI", hrv: "HR", hun: "HU", rou: "RO", bgr: "BG",
  grc: "GR", mlt: "MT", cyp: "CY", est: "EE", lva: "LV", ltu: "LT", alb: "AL", srb: "RS",
  usa: "US", can: "CA", mex: "MX", bra: "BR", arg: "AR", chl: "CL", col: "CO", per: "PE",
  aus: "AU", nzl: "NZ", jpn: "JP", chn: "CN", ind: "IN", kor: "KR", sgp: "SG", tha: "TH",
  are: "AE", sau: "SA", isr: "IL", tur: "TR", rus: "RU", ukr: "UA", egy: "EG", mar: "MA",
  tun: "TN", zaf: "ZA", nga: "NG", smr: "SM", vat: "VA", mco: "MC",
};

const flagOf = (a2) => a2 ? String.fromCodePoint(...[...a2].map((c) => 127397 + c.charCodeAt(0))) : "";
const regionNames = new Intl.DisplayNames(["it"], { type: "region" });
const countryLabel = (a3) => {
  const a2 = A3_TO_A2[a3?.toLowerCase()];
  if (!a2) return { flag: "", name: (a3 || "").toUpperCase() };
  let name = a2;
  try { name = regionNames.of(a2) || a2; } catch (e) { /* codice sconosciuto */ }
  return { flag: flagOf(a2), name };
};

const Bar = ({ pct, color = "bg-sky-400/70" }) => (
  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden flex-1">
    <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(pct, 2)}%` }} />
  </div>
);

export const CountryBars = ({ countries }) => {
  const total = countries.reduce((t, c) => t + c.clicks, 0) || 1;
  return (
    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="countries-section">
      <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 mb-2">Provenienza dei clic per Paese</p>
      <div className="space-y-1.5">
        {countries.slice(0, 6).map((c) => {
          const { flag, name } = countryLabel(c.country);
          const pct = Math.round((c.clicks / total) * 100);
          return (
            <div key={c.country} className="flex items-center gap-2 text-xs" data-testid={`country-row-${c.country}`}>
              <span className="w-32 truncate text-white/75">{flag} {name}</span>
              <Bar pct={pct} />
              <span className="w-16 text-right text-white/50">{c.clicks} clic</span>
              <span className="w-10 text-right font-semibold text-sky-300">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DEVICE_META = {
  DESKTOP: { label: "Computer", icon: Desktop, color: "bg-emerald-400/70", text: "text-emerald-300" },
  MOBILE: { label: "Smartphone", icon: DeviceMobile, color: "bg-sky-400/70", text: "text-sky-300" },
  TABLET: { label: "Tablet", icon: DeviceTablet, color: "bg-amber-400/70", text: "text-amber-300" },
};

export const DeviceBars = ({ devices }) => {
  const total = devices.reduce((t, d) => t + d.clicks, 0) || 1;
  return (
    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="devices-section">
      <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 mb-2">Dispositivi (clic)</p>
      <div className="space-y-1.5">
        {devices.map((d) => {
          const meta = DEVICE_META[d.device] || { label: d.device, icon: Question, color: "bg-white/30", text: "text-white/60" };
          const Icon = meta.icon;
          const pct = Math.round((d.clicks / total) * 100);
          return (
            <div key={d.device} className="flex items-center gap-2 text-xs" data-testid={`device-row-${d.device}`}>
              <span className="w-32 truncate text-white/75 inline-flex items-center gap-1.5">
                <Icon size={13} weight="duotone" className={meta.text} /> {meta.label}
              </span>
              <Bar pct={pct} color={meta.color} />
              <span className="w-16 text-right text-white/50">{d.clicks} clic</span>
              <span className={`w-10 text-right font-semibold ${meta.text}`}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const TopPagesList = ({ pages, domain }) => {
  const max = Math.max(...pages.map((p) => p.clicks), 1);
  return (
    <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="top-pages-section">
      <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 mb-2">Pagine top per clic</p>
      <div className="space-y-1.5">
        {pages.slice(0, 6).map((p) => (
          <div key={p.url} className="flex items-center gap-2 text-xs" data-testid={`top-page-row`}>
            <span className="flex-1 truncate text-white/75" title={p.url}>
              {p.url.replace(/^https?:\/\/(www\.)?/, "").replace(domain, "") || "/"}
            </span>
            <Bar pct={(p.clicks / max) * 100} color="bg-violet-400/70" />
            <span className="w-14 text-right font-semibold text-violet-300">{p.clicks}</span>
            <span className="w-14 text-right text-white/40">{p.impressions} imp.</span>
          </div>
        ))}
      </div>
    </div>
  );
};
